// Shared UI class helpers (variants, focus rings, link colors) used across reusable components.
//
// Pure values only, no hooks or context and no "use client": server components (the RSC routes)
// render the same primitives (Button, Input) and need to call these. Anything stateful belongs in
// the component file that uses it (form.tsx keeps useFormField).

import { cva } from 'class-variance-authority';

export const focusRingClassNames = [
    'focus-visible:outline-2',
    'focus-visible:outline-offset-2',
    'focus-visible:outline-solid',
    'focus-visible:outline-focus-ring',
].join(' ');

const toggleVariants = cva(
    `inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium hover:bg-muted hover:text-muted-foreground disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none transition-[color,box-shadow] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive whitespace-nowrap ${focusRingClassNames}`,
    {
        variants: {
            variant: {
                default: 'bg-transparent',
                outline:
          'border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground',
            },
            size: {
                default: 'h-9 px-2 min-w-9',
                sm: 'h-8 px-1.5 min-w-8',
                lg: 'h-10 px-2.5 min-w-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

const spinnerVariants = cva(
    'relative block opacity-[0.65]',
    {
        variants: {
            size: {
                sm: 'w-4 h-4',
                md: 'w-6 h-6',
                lg: 'w-8 h-8',
            },
        },
        defaultVariants: {
            size: 'sm',
        },
    },
);

const navigationMenuTriggerStyle = cva(
    'group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 data-[state=open]:hover:bg-accent data-[state=open]:text-accent-foreground data-[state=open]:focus:bg-accent data-[state=open]:bg-accent/50 focus-visible:ring-ring/50 outline-none transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1',
);

// Base variant styles (defined first for inheritance pattern)
const primaryStyles = [
    'bg-primary',
    'border-2 border-primary',
    'text-primary-foreground',
    'hover:bg-primary-accent',
    'hover:border-primary-accent',
    'hover:text-primary-foreground',
    'focus-visible:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
    'focus-visible:border-primary-accent',
    'focus-visible:text-primary-foreground',
    'light:bg-gz-marine-700',
    'light:border-gz-marine-700',
    'light:text-primary-foreground',
    'light:hover:bg-gz-marine-800',
    'light:hover:border-gz-marine-800',
    'light:hover:text-primary-foreground',
    'light:focus-visible:outline-gz-marine-700',
    'light:focus-visible:border-gz-marine-700',
    'light:focus-visible:text-primary-foreground',
    'light:active:bg-gz-marine-900',
    'light:active:border-gz-marine-900',
    'light:active:text-primary-foreground',
    'dark:bg-gz-marine-500',
    'dark:border-gz-marine-500',
    'dark:text-gz-neutral-800',
    'dark:hover:bg-gz-marine-700',
    'dark:hover:text-foreground',
    'dark:hover:border-gz-marine-700',
    'dark:focus-visible:outline-white',
    'dark:focus-visible:border-gz-marine-500',
    'dark:focus-visible:text-gz-neutral-800',
    'dark:active:bg-gz-marine-300',
    'dark:active:border-gz-marine-300',
    'dark:active:text-gz-neutral-800',
].join(' ');

const outlineStyles = [
    'bg-transparent',
    'border-2 border-secondary-accent',
    'text-background',
    'hover:bg-secondary-accent',
    'hover:border-secondary-accent',
    'hover:text-foreground',
    'focus-visible:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
    'focus-visible:bg-secondary-accent',
    'focus-visible:border-secondary-accent',
    'focus-visible:text-foreground',
    'light:border-gz-marine-700',
    'light:text-gz-neutral-800',
    'light:hover:bg-gz-marine-800',
    'light:hover:border-gz-marine-800',
    'light:hover:text-background',
    'light:focus-visible:bg-gz-marine-700',
    'light:focus-visible:border-gz-marine-700',
    'light:focus-visible:text-background',
    'light:focus-visible:outline-gz-marine-700',
    'light:active:bg-gz-marine-800',
    'light:active:border-gz-marine-800',
    'light:active:text-background',
    'dark:border-gz-marine-500',
    'dark:text-foreground',
    'dark:hover:bg-gz-marine-500',
    'dark:hover:border-gz-marine-500',
    'dark:hover:text-gz-neutral-800',
    'dark:focus-visible:bg-gz-marine-500',
    'dark:focus-visible:border-gz-marine-500',
    'dark:focus-visible:text-gz-neutral-800',
    'dark:focus-visible:outline-white',
    'dark:active:bg-gz-marine-300',
    'dark:active:border-gz-marine-300',
    'dark:active:text-gz-neutral-800',
].join(' ');

const ghostStyles = [
    'bg-transparent',
    'border border-transparent',
    'text-background',
    'hover:bg-secondary-accent',
    'hover:text-accent-foreground',
    'focus-visible:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
    'focus-visible:bg-secondary-accent',
    'focus-visible:border-secondary-accent',
    'focus-visible:text-foreground',
    'light:text-gz-neutral-800',
    'light:hover:text-gz-neutral-800',
    'light:focus-visible:bg-transparent',
    'light:focus-visible:border-gz-marine-700',
    'light:focus-visible:text-gz-neutral-800',
    'light:focus-visible:outline-gz-marine-700',
    'dark:text-foreground',
    'dark:hover:text-gz-neutral-800',
    'dark:focus-visible:text-gz-neutral-800',
    'dark:focus-visible:outline-white',
].join(' ');

const linkStyles = [
    'bg-transparent',
    // Single continuous underline under label + icon (text-decoration splits per flex child)
    'border-0 border-b border-solid border-current',
    '!rounded-none',
    'h-auto min-h-0',
    'type-caption font-normal',
    'pb-px',
    'focus-visible:ring-0',
    'focus-visible:outline-solid',
    'focus-visible:outline-2',
    'focus-visible:outline-offset-2',
    'light:focus-visible:outline-gz-marine-700',
    'dark:focus-visible:outline-white',
].join(' ');

/**
 * GoZig Button link — Default type.
 * Marine underline link; use with `variant="link"` (also the default when `color` is omitted).
 */
export const themeColorLinkClassNames
    = [
        'light:text-gz-marine-700 light:hover:text-gz-marine-800 light:focus-visible:text-gz-marine-700',
        'dark:text-gz-marine-500 dark:hover:text-gz-marine-300 dark:focus-visible:text-gz-marine-500',
    ].join(' ');

/** @deprecated Prefer omitting `color` — defaults to theme. Alias of `themeColorLinkClassNames`. */
export const defaultLinkColor = themeColorLinkClassNames;

/**
 * GoZig Button link — Back type.
 * No underline; gray → darker (light) / marine (dark) on hover. Pass as `color={backLinkClassNames}`.
 */
export const backLinkClassNames
    = [
        'border-b-0',
        'light:text-gz-neutral-600 light:hover:text-gz-neutral-800 light:focus-visible:text-gz-neutral-600',
        'dark:text-gz-neutral-200 dark:hover:text-gz-marine-500 dark:focus-visible:text-gz-neutral-200',
    ].join(' ');

// Size variant styles (using array join for readability)
/** Compact on mobile; from `lg` up, matches `small` padding/height/icon size (type stays 13px). */
const xsmallSizeStyles = [
    'h-auto min-h-7',
    'rounded-full',
    'gap-1',
    'px-2.5 py-1',
    'has-[>svg]:px-2',
    'has-[>span[class*="material-symbols"]]:px-2',
    '[&_svg:not([class*=\'size-\'])]:size-3.5',
    'type-button-xsmall',
    'lg:h-[36px]',
    'lg:min-h-0',
    'lg:px-4',
    'lg:py-0',
    'lg:has-[>svg]:px-3',
    'lg:has-[>span[class*="material-symbols"]]:px-3',
    'lg:[&_svg:not([class*=\'size-\'])]:size-4',
].join(' ');

const smallSizeStyles = [
    'h-[36px]',
    'rounded-full',
    'gap-1',
    'px-4',
    'has-[>svg]:px-3',
    'has-[>span[class*="material-symbols"]]:px-3',
    '[&_svg:not([class*=\'size-\'])]:size-4',
    'type-button-small',
].join(' ');

const defaultSizeStyles = [
    'h-[42px]',
    'rounded-full',
    'gap-1',
    'px-6',
    'has-[>svg]:px-4',
    'has-[>span[class*="material-symbols"]]:px-4',
    '[&_svg:not([class*=\'size-\'])]:size-4',
    'type-button-default',
].join(' ');

const largeSizeStyles = [
    'h-[52px]',
    'rounded-full',
    'gap-1',
    'px-8',
    'has-[>svg]:px-6',
    'has-[>span[class*="material-symbols"]]:px-6',
    '[&_svg:not([class*=\'size-\'])]:size-6',
    'type-button-large',
].join(' ');

const smSizeStyles = [
    'h-[36px]',
    'rounded-full',
    'gap-1',
    'px-3',
    'has-[>svg]:px-3',
    'has-[>span[class*="material-symbols"]]:px-3',
    '[&_svg:not([class*=\'size-\'])]:size-4',
    'type-button-small',
].join(' ');

const lgSizeStyles = [
    'h-[52px]',
    'rounded-full',
    'gap-1.5',
    'px-8',
    'has-[>svg]:px-6',
    'has-[>span[class*="material-symbols"]]:px-6',
    '[&_svg:not([class*=\'size-\'])]:size-6',
    'type-button-large',
].join(' ');

const iconSizeStyles = [
    'size-8',
    '!rounded-[4px]',
    'p-1',
    'gap-0',
    '[&_svg:not([class*=\'size-\'])]:size-6',
    '[&_span[class*="material-symbols"]]:!text-2xl',
    '[&_span[class*="material-symbols"]]:leading-none',
].join(' ');

const iconLgSizeStyles = [
    'size-10',
    '!rounded-[8px]',
    'p-1',
    'gap-0',
    '[&_svg:not([class*=\'size-\'])]:!size-8',
    '[&_span[class*="material-symbols"]]:!text-[32px]',
    '[&_span[class*="material-symbols"]]:leading-none',
].join(' ');

const iconContainedHoverShadow = 'hover:shadow-[0px_1px_2px_0px_rgba(35,33,31,0.2)]';

const iconGhostCompoundStyles = [
    'hover:bg-transparent',
    'light:hover:bg-transparent',
    'dark:hover:bg-transparent',
    'light:hover:!text-gz-neutral-800',
    'dark:hover:!text-foreground',
    'focus-visible:bg-transparent',
    'light:focus-visible:bg-transparent',
    'dark:focus-visible:bg-transparent',
    'light:focus-visible:!text-gz-neutral-800',
    'dark:focus-visible:!text-foreground',
    'light:focus-visible:border-transparent',
    'dark:focus-visible:border-transparent',
].join(' ');

const iconContainedSmallCompoundStyles = [
    '!size-6',
    '!rounded-[4px]',
    '!border !border-solid',
    'p-1',
    'shadow-none',
    '[&_svg:not([class*=\'size-\'])]:!size-4',
    '[&_span[class*="material-symbols"]]:!text-base',
    '[&_span[class*="material-symbols"]]:leading-none',
    iconContainedHoverShadow,
    'light:!bg-white',
    'light:!border-gz-neutral-200',
    'light:!text-gz-neutral-800',
    'light:hover:!bg-white',
    'light:hover:!border-gz-neutral-200',
    'light:hover:!text-gz-neutral-800',
    'light:focus-visible:!bg-white',
    'light:focus-visible:!border-gz-neutral-200',
    'light:focus-visible:!text-gz-neutral-800',
    'light:focus-visible:outline-gz-marine-700',
    'light:active:!bg-white',
    'light:active:!border-gz-neutral-200',
    'light:active:!text-gz-neutral-800',
    'dark:!bg-gz-neutral-500',
    'dark:!border-transparent',
    'dark:!text-foreground',
    'dark:hover:!bg-gz-neutral-500',
    'dark:hover:!border-transparent',
    'dark:hover:!text-foreground',
    'dark:focus-visible:!bg-gz-neutral-500',
    'dark:focus-visible:!border-transparent',
    'dark:focus-visible:!text-foreground',
    'dark:focus-visible:outline-white',
    'dark:active:!bg-gz-neutral-500',
    'dark:active:!border-transparent',
    'dark:active:!text-foreground',
].join(' ');

const iconContainedLargeCompoundStyles = [
    '!size-10',
    '!rounded-[8px]',
    '!border !border-solid',
    'p-1',
    'shadow-none',
    '[&_svg:not([class*=\'size-\'])]:!size-8',
    '[&_span[class*="material-symbols"]]:!text-[32px]',
    '[&_span[class*="material-symbols"]]:leading-none',
    iconContainedHoverShadow,
    'light:!bg-white',
    'light:!border-gz-neutral-200',
    'light:!text-gz-neutral-800',
    'light:hover:!bg-white',
    'light:hover:!border-gz-neutral-200',
    'light:hover:!text-gz-neutral-800',
    'light:focus-visible:!bg-white',
    'light:focus-visible:!border-gz-neutral-200',
    'light:focus-visible:!text-gz-neutral-800',
    'light:focus-visible:outline-gz-marine-700',
    'light:active:!bg-white',
    'light:active:!border-gz-neutral-200',
    'light:active:!text-gz-neutral-800',
    'dark:!bg-transparent',
    'dark:!border-gz-neutral-200',
    'dark:!text-foreground',
    'dark:hover:!bg-transparent',
    'dark:hover:!border-gz-neutral-200',
    'dark:hover:!text-foreground',
    'dark:focus-visible:!bg-transparent',
    'dark:focus-visible:!border-gz-neutral-200',
    'dark:focus-visible:!text-foreground',
    'dark:focus-visible:outline-white',
    'dark:active:!bg-transparent',
    'dark:active:!border-gz-neutral-200',
    'dark:active:!text-foreground',
].join(' ');

// Destructive variant for delete/danger actions
const destructiveStyles = [
    'bg-gz-red-500',
    'border-2 border-gz-red-500',
    'text-white',
    'hover:bg-gz-red-600',
    'hover:border-gz-red-600',
    'hover:text-white',
    'focus-visible:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
    'focus-visible:bg-gz-red-500',
    'focus-visible:border-gz-red-500',
    'focus-visible:text-white',
    'light:focus-visible:outline-gz-marine-700',
    'light:active:bg-gz-red-700',
    'light:active:border-gz-red-700',
    'light:active:text-white',
    'dark:hover:bg-gz-red-400',
    'dark:hover:border-gz-red-400',
    'dark:hover:text-gz-neutral-800',
    'dark:focus-visible:outline-white',
    'dark:active:bg-gz-red-300',
    'dark:active:border-gz-red-300',
    'dark:active:text-gz-neutral-800',
].join(' ');

// Tertiary inherits from outline, then adds overrides
const tertiaryStyles = [
    outlineStyles,
    'border-secondary-vibrant',
    'hover:text-background',
    'hover:bg-secondary-vibrant',
    'hover:border-secondary-vibrant',
    'focus-visible:bg-secondary-vibrant',
    'focus-visible:border-secondary-vibrant',
    'focus-visible:text-background',
    'light:border-gz-marine-500',
    'light:hover:text-gz-neutral-800',
    'light:hover:bg-gz-marine-500',
    'light:hover:border-gz-marine-500',
    'light:focus-visible:bg-gz-marine-500',
    'light:focus-visible:border-gz-marine-500',
    'light:focus-visible:text-gz-neutral-800',
    'light:focus-visible:outline-gz-marine-500',
    'light:active:bg-gz-marine-600',
    'light:active:border-gz-marine-600',
    'light:active:text-gz-neutral-800',
    'dark:border-gz-blue-500',
    'dark:hover:text-foreground',
    'dark:hover:bg-gz-blue-500',
    'dark:hover:border-gz-blue-500',
    'dark:focus-visible:bg-gz-blue-500',
    'dark:focus-visible:border-gz-blue-500',
    'dark:focus-visible:text-foreground',
    'dark:focus-visible:outline-white',
    'dark:active:bg-gz-blue-400',
    'dark:active:border-gz-blue-400',
    'dark:active:text-gz-neutral-800',
].join(' ');

const buttonVariants = cva(
    `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive ${focusRingClassNames}`,
    {
        variants: {
            variant: {
                // Utility Variants (defined first as base variants)
                // NOTE: These are utility variants for special cases, NOT design system variants
                // For new code, prefer design system variants (primary, secondary, tertiary)
                outline: outlineStyles, // Utility variant - use 'secondary' or 'tertiary' for design system buttons
                ghost: ghostStyles, // Utility variant
                link: linkStyles, // Utility variant (set text via color prop or className)
                destructive: destructiveStyles, // Utility variant - for delete/danger actions

                // Design System Variants (preferred for new code)
                // These are the official design system variants that should be used in components
                primary: primaryStyles, // Design system: Primary filled button
                secondary: outlineStyles, // Design system: Standard outline button (same styles as outline, but design system variant)
                tertiary: tertiaryStyles, // Design system: Outline button with tertiary color scheme

                // Backward Compatibility (deprecated - use design system variants instead)
                // NOTE: These are kept for backward compatibility only
                // When updating components, replace 'default' with 'primary' and 'outline' with 'secondary' or 'tertiary'
                default: primaryStyles, // Deprecated: Use 'primary' instead
            },
            size: {
                // Design System Sizes
                // SVG sizing: xsmall size-3.5; small/default use size-4 (16px); large uses size-6 (24px)
                xsmall: xsmallSizeStyles,
                small: smallSizeStyles,
                default: defaultSizeStyles,
                large: largeSizeStyles,

                // Backward Compatibility Aliases
                xs: xsmallSizeStyles,
                sm: smSizeStyles,
                lg: lgSizeStyles,

                icon: iconSizeStyles,
                iconLg: iconLgSizeStyles,
            },
        },
        compoundVariants: [
            {
                variant: 'link',
                class: [
                    '!px-0 !py-0 h-auto min-h-0',
                    'has-[>svg]:!px-0 has-[>span[class*="material-symbols"]]:!px-0',
                    'gap-1',
                    '[&_svg:not([class*=\'size-\'])]:!size-5',
                    '[&_span[class*="material-symbols"]]:!text-xl',
                ].join(' '),
            },
            {
                variant: 'ghost',
                size: 'icon',
                class: iconGhostCompoundStyles,
            },
            {
                variant: 'ghost',
                size: 'iconLg',
                class: iconGhostCompoundStyles,
            },
            {
                variant: 'secondary',
                size: 'icon',
                class: iconContainedSmallCompoundStyles,
            },
            {
                variant: 'outline',
                size: 'icon',
                class: iconContainedSmallCompoundStyles,
            },
            {
                variant: 'secondary',
                size: 'iconLg',
                class: iconContainedLargeCompoundStyles,
            },
            {
                variant: 'outline',
                size: 'iconLg',
                class: iconContainedLargeCompoundStyles,
            },
        ],
        defaultVariants: {
            variant: 'primary',
            size: 'default',
        },
    },
);

const badgeVariants = cva(
    `inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden ${focusRingClassNames}`,
    {
        variants: {
            variant: {
                default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
                secondary:
          'border-transparent bg-gray-extralight text-secondary-foreground [a&]:hover:bg-secondary/90',
                destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
                outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
                detailSheet:
          'border-none rounded-lg px-3 py-0.5 text-sm font-normal flex items-center gap-2 bg-white/20 light:bg-gz-neutral-100 dark:bg-foreground/20 text-white light:text-foreground dark:text-foreground',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

/** Floating help tooltip body (Radix TooltipContent, desktop Popover in ResponsiveTooltip, etc.) */
const tooltipContentClassNames = [
    'max-w-[min(18rem,calc(100vw-2rem))]',
    'px-3 py-2',
    'border border-border',
    'shadow-md',
    'type-caption',
    'light:bg-gz-neutral-100 light:border-gz-neutral-200 light:text-gz-neutral-800 dark:bg-gz-blue-900 dark:border-gz-neutral-400 dark:text-gz-neutral-100',
].join(' ');

const tabsVariants = cva(
    [
        // Base styles
        'inline-flex items-center justify-center text-sm font-medium whitespace-nowrap transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=\'size-\'])]:size-4',
        focusRingClassNames,
    ],
    {
        variants: {
            variant: {
                default: [
                    // Base styles for default variant
                    'rounded-sm light:border dark:border px-4 py-2',
                    // Inactive state (base)
                    'bg-secondary light:bg-transparent dark:bg-transparent light:border-primary light:text-gz-neutral-800 light:hover:bg-gz-marine-800 light:hover:border-gz-marine-800 light:hover:text-background light:focus-visible:bg-primary light:focus-visible:border-primary light:focus-visible:text-background dark:border-accent dark:text-foreground dark:hover:bg-accent dark:hover:border-accent dark:hover:text-gz-neutral-800 dark:focus-visible:bg-accent dark:focus-visible:border-accent dark:focus-visible:text-gz-neutral-800 text-white',
                    // Active state (overrides when data-state=active)
                    'data-[state=active]:bg-secondary-vibrant data-[state=active]:light:bg-primary data-[state=active]:light:border-primary data-[state=active]:light:text-background data-[state=active]:light:hover:bg-gz-marine-800 data-[state=active]:light:hover:border-gz-marine-800 data-[state=active]:light:focus-visible:border-primary data-[state=active]:dark:bg-accent data-[state=active]:dark:border-accent data-[state=active]:dark:text-gz-neutral-800 data-[state=active]:dark:hover:bg-accent-darker data-[state=active]:dark:hover:text-foreground data-[state=active]:dark:hover:border-accent-darker data-[state=active]:dark:focus-visible:border-accent',
                ].join(' '),
                line: [
                    // Base styles for line variant (underline indicator)
                    'relative px-0 pb-2 text-base font-semibold',
                    'border-t-0 border-x-0 border-b-2 border-transparent',
                    'bg-transparent shadow-none rounded-none',
                    // Inactive state
                    'text-white light:text-gz-neutral-800 dark:text-foreground',
                    // Active state (underline indicator)
                    'data-[state=active]:text-white light:data-[state=active]:text-gz-neutral-800 dark:data-[state=active]:text-foreground data-[state=active]:font-bold data-[state=active]:border-secondary-accent light:data-[state=active]:border-primary dark:data-[state=active]:border-accent data-[state=active]:bg-transparent',
                ].join(' '),
                pill: [
                    'relative flex flex-1 items-center justify-center px-4 py-3 type-body-default transition-colors border-none shadow-none rounded-none font-normal whitespace-normal',
                    'data-[state=active]:font-medium light:data-[state=active]:bg-gz-marine-100 light:data-[state=active]:hover:bg-gz-marine-800 light:data-[state=active]:text-gz-neutral-800 light:data-[state=active]:hover:text-gz-neutral-white dark:data-[state=active]:bg-gz-blue-500 dark:data-[state=active]:hover:bg-gz-blue-600 dark:data-[state=active]:text-foreground',
                    'light:data-[state=inactive]:text-gz-neutral-800 dark:data-[state=inactive]:text-gz-neutral-100 light:data-[state=inactive]:hover:bg-gz-marine-800 dark:data-[state=inactive]:hover:bg-gz-blue-600 light:data-[state=inactive]:hover:text-gz-neutral-white',
                ].join(' '),
                /** Same colors as `pill` but with tighter padding. */
                pillSm: [
                    'relative flex flex-1 items-center justify-center px-3 py-2 type-body-default transition-colors border-none shadow-none rounded-none font-normal whitespace-normal',
                    'data-[state=active]:font-medium light:data-[state=active]:bg-gz-marine-100 light:data-[state=active]:hover:bg-gz-marine-800 light:data-[state=active]:text-gz-neutral-800 light:data-[state=active]:hover:text-gz-neutral-white dark:data-[state=active]:bg-gz-blue-500 dark:data-[state=active]:hover:bg-gz-blue-600 dark:data-[state=active]:text-foreground',
                    'light:data-[state=inactive]:text-gz-neutral-800 dark:data-[state=inactive]:text-gz-neutral-100 light:data-[state=inactive]:hover:bg-gz-marine-800 dark:data-[state=inactive]:hover:bg-gz-blue-600 light:data-[state=inactive]:hover:text-gz-neutral-white',
                ].join(' '),
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

export {
    badgeVariants,
    buttonVariants,
    navigationMenuTriggerStyle,
    spinnerVariants,
    tabsVariants,
    toggleVariants,
    tooltipContentClassNames,
};
